import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus, Search, Edit3, Trash2, X, LogOut, Image as ImageIcon, Loader2, Upload,
  Tag, FolderPlus, Pencil, Save, Sparkles, CheckCircle2, AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { api, formatApiError } from '@/lib/api';
import Logo from '@/components/site/Logo';
import { productImageUrl } from '@/lib/imageUrl';
import { invalidateCatalog } from '@/hooks/useCatalog';

const MAX_BYTES = 800 * 1024; // 800 KB

const EMPTY = {
  slug: '', label: '', category: 'lehengas', sub: '', image: '', image_seo_name: '',
  fabric: '', color: '', occasion: '', inclusions: '', sizes: 'Custom sizing · XS – XXL',
  care: 'Dry clean only', price: 'On request', description: '', tags: '',
  sold: false, is_category_hero: false,
};

function fileToDataUrl(file) {
  return new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(r.result);
    r.onerror = rej;
    r.readAsDataURL(file);
  });
}

const Field = ({ label, children }) => (
  <div>
    <label className="overline">{label}</label>
    {children}
  </div>
);

const inputCls = 'w-full bg-transparent border-b border-brandborder focus:border-maroon outline-none py-2.5 text-charcoal placeholder-charcoal/40 text-sm transition-colors';

// --- Product form (modal) ---
function ProductForm({ open, onClose, initial, categories, onSaved }) {
  const isEdit = !!initial?.slug;
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState('');

  useEffect(() => {
    if (open) {
      const seed = initial
        ? { ...EMPTY, ...initial, tags: (initial.tags || []).join(', ') }
        : EMPTY;
      setForm(seed);
      setPreview(seed.image || '');
    }
  }, [open, initial]);

  const upd = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));
  const updBool = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.checked }));

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_BYTES) {
      toast.error(`Image too large. Max ${(MAX_BYTES / 1024).toFixed(0)} KB. Yours: ${(file.size / 1024).toFixed(0)} KB.`);
      return;
    }
    const data = await fileToDataUrl(file);
    setForm((s) => ({ ...s, image: data }));
    setPreview(data);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.label || !form.image || !form.category) {
      toast.error('Label, collection and image are required.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        tags: form.tags.split(',').map((t) => t.trim()).filter(Boolean),
      };
      if (isEdit) {
        const { slug, ...patch } = payload;
        await api.put(`/products/${initial.slug}`, patch);
        toast.success('Product updated');
      } else {
        await api.post('/products', payload);
        toast.success('Product added');
      }
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err, 'Could not save'));
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-charcoal/60 backdrop-blur-sm flex items-start lg:items-center justify-center p-4 overflow-y-auto" data-testid="product-form-modal">
      <motion.div
        initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
        className="bg-ivory w-full max-w-4xl my-8 border border-brandborder shadow-card"
      >
        <div className="flex items-center justify-between px-8 py-5 border-b border-brandborder">
          <div>
            <span className="overline">{isEdit ? 'Edit Product' : 'New Product'}</span>
            <div className="font-display text-2xl text-charcoal mt-1">{isEdit ? initial.label : 'Add to the collection'}</div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-blush rounded-full" aria-label="Close" data-testid="form-close"><X className="w-5 h-5" /></button>
        </div>

        <form onSubmit={submit} className="grid lg:grid-cols-12 gap-8 p-8">
          {/* Image */}
          <div className="lg:col-span-5">
            <div className="aspect-[3/4] bg-blush border border-brandborder overflow-hidden flex items-center justify-center">
              {preview ? (
                <img src={preview} alt="preview" className="w-full h-full object-cover" />
              ) : (
                <div className="text-charcoal/40 text-sm flex flex-col items-center gap-2">
                  <ImageIcon className="w-8 h-8" /> No image
                </div>
              )}
            </div>
            <div className="mt-4 flex flex-col gap-3">
              <label className="inline-flex items-center justify-center gap-2 px-5 py-3 border border-brandborder hover:border-maroon hover:text-maroon text-xs uppercase tracking-[0.18em] cursor-pointer transition-colors">
                <Upload className="w-4 h-4" /> Upload from device
                <input type="file" accept="image/*" className="hidden" onChange={handleFile} data-testid="form-file" />
              </label>
              <Field label="Or paste image URL">
                <input value={form.image.startsWith('data:') ? '' : form.image} onChange={(e) => { setForm((s) => ({ ...s, image: e.target.value })); setPreview(e.target.value); }} className={inputCls} placeholder="https://…" data-testid="form-image-url" />
              </Field>
              <p className="text-[11px] text-charcoal/55">Max {(MAX_BYTES / 1024).toFixed(0)} KB if uploading. URLs have no size limit.</p>
            </div>
          </div>

          {/* Fields */}
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-5">
            <Field label="Name *"><input value={form.label} onChange={upd('label')} className={inputCls} required data-testid="form-label" /></Field>
            <Field label="Slug (URL)">
              <input value={form.slug} onChange={upd('slug')} className={inputCls} placeholder="auto from name" disabled={isEdit} data-testid="form-slug" />
            </Field>
            <div className="sm:col-span-2">
              <Field label="SEO Image filename (auto-generated · optional override)">
                <input
                  value={form.image_seo_name}
                  onChange={upd('image_seo_name')}
                  className={inputCls}
                  placeholder="e.g. pink-bridal-lehenga-morrisville-nc.webp"
                  data-testid="form-image-seo-name"
                />
              </Field>
              <p className="text-[11px] text-charcoal/55 mt-1">
                Leave blank to auto-generate as <code className="text-charcoal/70">{`{color}-{slug}-morrisville-nc.webp`}</code>. Used for Google Image search filenames — no spaces, use hyphens.
              </p>
            </div>
            <Field label="Collection *">
              <select value={form.category} onChange={upd('category')} className={`${inputCls} appearance-none`} required data-testid="form-category">
                {categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </Field>
            <Field label="Price"><input value={form.price} onChange={upd('price')} className={inputCls} data-testid="form-price" /></Field>
            <Field label="Short subtitle"><input value={form.sub} onChange={upd('sub')} className={inputCls} data-testid="form-sub" /></Field>
            <Field label="Colour"><input value={form.color} onChange={upd('color')} className={inputCls} /></Field>
            <div className="sm:col-span-2">
              <Field label="Fabric / Highlights (multi-line & bullets)">
                <textarea
                  value={form.fabric}
                  onChange={upd('fabric')}
                  data-testid="form-fabric"
                  rows={4}
                  placeholder={"e.g.\n- Pure silk with zardosi work\n- Blouse with heavy embroidery\n- Comes with net dupatta"}
                  className={`${inputCls} resize-none`}
                />
              </Field>
            </div>
            <Field label="Occasion"><input value={form.occasion} onChange={upd('occasion')} className={inputCls} /></Field>
            <Field label="Inclusions"><input value={form.inclusions} onChange={upd('inclusions')} className={inputCls} /></Field>
            <Field label="Sizing"><input value={form.sizes} onChange={upd('sizes')} className={inputCls} /></Field>
            <Field label="Care"><input value={form.care} onChange={upd('care')} className={inputCls} /></Field>
            <div className="sm:col-span-2">
              <Field label="Tags (comma-separated)">
                <input value={form.tags} onChange={upd('tags')} className={inputCls} placeholder="red, bridal, sequin, wedding" data-testid="form-tags" />
              </Field>
            </div>
            <div className="sm:col-span-2">
              <Field label="Description (long)">
                <textarea value={form.description} onChange={upd('description')} rows={3} className={`${inputCls} resize-none`} />
              </Field>
            </div>

            {/* Product state toggles */}
            <div className="sm:col-span-2 pt-4 border-t border-brandborder space-y-3">
              <label className="flex items-start gap-3 cursor-pointer group" data-testid="form-hero-wrapper">
                <input
                  type="checkbox"
                  checked={!!form.is_category_hero}
                  onChange={updBool('is_category_hero')}
                  data-testid="form-is-category-hero"
                  className="mt-1 w-4 h-4 accent-maroon"
                />
                <div>
                  <div className="text-sm font-medium text-charcoal group-hover:text-maroon transition-colors">
                    Set as Category Image
                  </div>
                  <div className="text-xs text-charcoal/60 mt-0.5">
                    Fixes this image as the hero tile for its collection in the Featured Collections gallery.
                    Only one product per category can be the hero — saving this replaces any previous hero.
                  </div>
                </div>
              </label>

              <label className="flex items-start gap-3 cursor-pointer group" data-testid="form-sold-wrapper">
                <input
                  type="checkbox"
                  checked={!!form.sold}
                  onChange={updBool('sold')}
                  data-testid="form-sold"
                  className="mt-1 w-4 h-4 accent-burgundy"
                />
                <div>
                  <div className="text-sm font-medium text-charcoal group-hover:text-maroon transition-colors">
                    Mark as SOLD
                  </div>
                  <div className="text-xs text-charcoal/60 mt-0.5">
                    Product remains visible in listings with a prominent SOLD ribbon overlay. Uncheck to bring it back to stock.
                  </div>
                </div>
              </label>
            </div>

            <div className="sm:col-span-2 flex items-center justify-end gap-3 pt-4 mt-2 border-t border-brandborder">
              <button type="button" onClick={onClose} className="px-6 py-3 text-xs uppercase tracking-[0.2em] text-charcoal/70 hover:text-maroon transition-colors">Cancel</button>
              <button type="submit" disabled={saving} data-testid="form-submit" className="inline-flex items-center gap-2 px-7 py-3 bg-burgundy hover:bg-maroon text-ivory text-xs uppercase tracking-[0.22em] transition-colors disabled:opacity-60">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {isEdit ? 'Save changes' : 'Add product'}
              </button>
            </div>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

// --- Categories manager (modal) ---
function CategoriesManager({ open, onClose, categories, onChanged }) {
  const [newKey, setNewKey] = useState('');
  const [newLabel, setNewLabel] = useState('');
  const [editing, setEditing] = useState({});

  if (!open) return null;

  const add = async () => {
    if (!newKey || !newLabel) { toast.error('Key and label required'); return; }
    try {
      await api.post('/categories', { key: newKey, label: newLabel, order: categories.length + 1 });
      toast.success('Collection added');
      setNewKey(''); setNewLabel('');
      onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const rename = async (key) => {
    try {
      await api.put(`/categories/${key}`, { label: editing[key] });
      toast.success('Renamed');
      setEditing((s) => ({ ...s, [key]: undefined }));
      onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const remove = async (key) => {
    if (!window.confirm('Delete this collection? Only allowed when no products use it.')) return;
    try { await api.delete(`/categories/${key}`); toast.success('Deleted'); onChanged(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-charcoal/60 backdrop-blur-sm flex items-center justify-center p-4">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-ivory w-full max-w-xl border border-brandborder">
        <div className="flex items-center justify-between px-7 py-5 border-b border-brandborder">
          <div className="font-display text-2xl">Manage Collections</div>
          <button onClick={onClose} className="p-2 hover:bg-blush rounded-full"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-7 space-y-4">
          {categories.map((c) => (
            <div key={c.key} className="flex items-center gap-3 py-2 border-b border-brandborder/60">
              <span className="overline w-24 shrink-0">{c.key}</span>
              {editing[c.key] != null ? (
                <input value={editing[c.key]} onChange={(e) => setEditing((s) => ({ ...s, [c.key]: e.target.value }))} className={`${inputCls} py-1`} />
              ) : (
                <span className="text-sm flex-1">{c.label}</span>
              )}
              {editing[c.key] != null ? (
                <button onClick={() => rename(c.key)} className="p-2 text-maroon hover:bg-blush rounded-full" aria-label="Save"><Save className="w-4 h-4" /></button>
              ) : (
                <button onClick={() => setEditing((s) => ({ ...s, [c.key]: c.label }))} className="p-2 text-charcoal/60 hover:text-maroon hover:bg-blush rounded-full" aria-label="Rename"><Pencil className="w-4 h-4" /></button>
              )}
              <button onClick={() => remove(c.key)} className="p-2 text-charcoal/60 hover:text-red-700 hover:bg-blush rounded-full" aria-label="Delete"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}

          <div className="pt-4 mt-2 border-t border-brandborder">
            <div className="overline mb-3">Add new collection</div>
            <div className="grid grid-cols-2 gap-3">
              <input className={inputCls} placeholder="key (e.g. gowns)" value={newKey} onChange={(e) => setNewKey(e.target.value)} />
              <input className={inputCls} placeholder="Label (e.g. Gowns)" value={newLabel} onChange={(e) => setNewLabel(e.target.value)} />
            </div>
            <button onClick={add} className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 bg-burgundy hover:bg-maroon text-ivory text-xs uppercase tracking-[0.2em]">
              <FolderPlus className="w-4 h-4" /> Add collection
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

// --- Bulk AI Upload Modal ---
const BULK_MAX = 10;
const BULK_MAX_BYTES = 800 * 1024; // 800 KB per image

function BulkAIUpload({ open, onClose, onSaved }) {
  const [items, setItems] = useState([]); // [{file, dataUrl, filename}]
  const [analyzing, setAnalyzing] = useState(false);
  const [results, setResults] = useState(null); // { total, created, failed, results: [] }

  const reset = () => {
    setItems([]);
    setResults(null);
    setAnalyzing(false);
  };

  const close = () => { reset(); onClose(); };

  const handleFiles = async (fileList) => {
    const arr = Array.from(fileList || []);
    if (!arr.length) return;
    if (items.length + arr.length > BULK_MAX) {
      toast.error(`Maximum ${BULK_MAX} images at a time. You can add ${BULK_MAX - items.length} more.`);
      return;
    }
    const next = [...items];
    for (const file of arr) {
      if (!file.type.startsWith('image/')) {
        toast.error(`${file.name}: not an image`);
        continue;
      }
      if (file.size > BULK_MAX_BYTES) {
        toast.error(`${file.name}: too large (max ${(BULK_MAX_BYTES / 1024).toFixed(0)} KB)`);
        continue;
      }
      const dataUrl = await fileToDataUrl(file);
      next.push({ filename: file.name, dataUrl });
    }
    setItems(next.slice(0, BULK_MAX));
  };

  const removeAt = (idx) => setItems((s) => s.filter((_, i) => i !== idx));

  const analyze = async () => {
    if (!items.length) {
      toast.error('Add at least one image.');
      return;
    }
    setAnalyzing(true);
    setResults(null);
    try {
      const payload = { images: items.map((it) => ({ image: it.dataUrl, filename: it.filename })) };
      const { data } = await api.post('/products/bulk-analyze', payload);
      setResults(data);
      if (data.created > 0) {
        toast.success(`AI created ${data.created} of ${data.total} products`);
        onSaved();
      }
      if (data.failed > 0) {
        toast.error(`${data.failed} image${data.failed === 1 ? '' : 's'} failed — see details below.`);
      }
    } catch (e) {
      toast.error(formatApiError(e, 'Bulk analysis failed'));
    } finally {
      setAnalyzing(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-charcoal/60 backdrop-blur-sm flex items-start lg:items-center justify-center p-4 overflow-y-auto" data-testid="bulk-ai-modal">
      <motion.div
        initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
        className="bg-ivory w-full max-w-3xl my-8 border border-brandborder shadow-card"
      >
        <div className="flex items-center justify-between px-8 py-5 border-b border-brandborder">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 border border-gold flex items-center justify-center text-maroon">
              <Sparkles className="w-5 h-5" />
            </div>
            <div>
              <span className="overline">AI Bulk Upload</span>
              <div className="font-display text-2xl text-charcoal mt-0.5">Upload up to {BULK_MAX} products</div>
            </div>
          </div>
          <button onClick={close} className="p-2 hover:bg-blush rounded-full" aria-label="Close" data-testid="bulk-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-8">
          {!results && (
            <>
              <p className="text-sm text-charcoal/70 mb-6">
                Drop up to <strong className="text-charcoal">{BULK_MAX} product photos</strong>. Our AI (Gemini 3 Flash) will analyze each image and auto-generate the product name, category, colour, fabric details, occasion and tags — then save them straight to your closet.
              </p>

              <label
                className="block border-2 border-dashed border-brandborder hover:border-maroon transition-colors p-10 text-center cursor-pointer bg-surface"
                data-testid="bulk-dropzone"
              >
                <Upload className="w-8 h-8 mx-auto text-charcoal/40" />
                <div className="mt-3 font-display text-lg">Click to select images</div>
                <div className="text-xs text-charcoal/55 mt-1">
                  JPEG · PNG · WEBP · Max {(BULK_MAX_BYTES / 1024).toFixed(0)} KB each · {items.length}/{BULK_MAX} selected
                </div>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  data-testid="bulk-file-input"
                  onChange={(e) => handleFiles(e.target.files)}
                />
              </label>

              {items.length > 0 && (
                <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                  {items.map((it, idx) => (
                    <div key={idx} className="relative border border-brandborder aspect-[3/4] overflow-hidden group" data-testid={`bulk-thumb-${idx}`}>
                      <img src={it.dataUrl} alt={it.filename} className="w-full h-full object-cover" />
                      <button
                        onClick={() => removeAt(idx)}
                        className="absolute top-1 right-1 w-7 h-7 rounded-full bg-ivory/90 hover:bg-maroon hover:text-ivory flex items-center justify-center transition-colors"
                        aria-label="Remove"
                        data-testid={`bulk-remove-${idx}`}
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                      <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent p-2">
                        <div className="text-[10px] text-white truncate">{it.filename}</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="mt-8 flex items-center justify-end gap-3 pt-4 border-t border-brandborder">
                <button
                  type="button"
                  onClick={close}
                  disabled={analyzing}
                  className="px-6 py-3 text-xs uppercase tracking-[0.2em] text-charcoal/70 hover:text-maroon transition-colors disabled:opacity-60"
                >
                  Cancel
                </button>
                <button
                  onClick={analyze}
                  disabled={analyzing || items.length === 0}
                  data-testid="bulk-analyze-btn"
                  className="inline-flex items-center gap-2 px-7 py-3 bg-burgundy hover:bg-maroon text-ivory text-xs uppercase tracking-[0.22em] transition-colors disabled:opacity-60"
                >
                  {analyzing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  {analyzing ? 'Analyzing…' : `Analyze & save ${items.length || ''}`.trim()}
                </button>
              </div>

              {analyzing && (
                <p className="mt-4 text-xs text-charcoal/55 text-center">
                  This can take 5–15 seconds per image while the AI reads each outfit. Please keep this window open.
                </p>
              )}
            </>
          )}

          {results && (
            <div data-testid="bulk-results">
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="border border-brandborder bg-surface p-4">
                  <div className="overline">Uploaded</div>
                  <div className="font-display text-3xl mt-1">{results.total}</div>
                </div>
                <div className="border border-brandborder bg-surface p-4">
                  <div className="overline text-maroon">Created</div>
                  <div className="font-display text-3xl mt-1 text-maroon">{results.created}</div>
                </div>
                <div className="border border-brandborder bg-surface p-4">
                  <div className="overline">Failed</div>
                  <div className={`font-display text-3xl mt-1 ${results.failed ? 'text-red-700' : 'text-charcoal/40'}`}>{results.failed}</div>
                </div>
              </div>

              <div className="space-y-3">
                {results.results.map((r) => (
                  <div key={r.index} className="border border-brandborder p-4 flex items-start gap-4" data-testid={`bulk-result-${r.index}`}>
                    {r.status === 'created' ? (
                      <CheckCircle2 className="w-5 h-5 text-maroon shrink-0 mt-0.5" />
                    ) : (
                      <AlertCircle className="w-5 h-5 text-red-700 shrink-0 mt-0.5" />
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-charcoal/55 truncate">{r.filename}</div>
                      {r.status === 'created' ? (
                        <>
                          <div className="font-display text-lg text-charcoal mt-0.5">{r.product.label}</div>
                          <div className="text-xs text-charcoal/60 mt-1">
                            {r.product.category} · {r.product.color} · {(r.product.tags || []).slice(0, 5).join(', ')}
                          </div>
                        </>
                      ) : (
                        <div className="text-sm text-red-700 mt-0.5">{r.error}</div>
                      )}
                    </div>
                    {r.status === 'created' && (
                      <a
                        href={`/product/${r.product.slug}`}
                        target="_blank"
                        rel="noreferrer"
                        className="text-[11px] uppercase tracking-[0.18em] text-maroon hover:underline shrink-0"
                      >
                        View →
                      </a>
                    )}
                  </div>
                ))}
              </div>

              <div className="mt-8 flex items-center justify-end gap-3 pt-4 border-t border-brandborder">
                <button
                  onClick={reset}
                  className="px-6 py-3 text-xs uppercase tracking-[0.2em] text-charcoal/70 hover:text-maroon transition-colors"
                  data-testid="bulk-upload-more"
                >
                  Upload more
                </button>
                <button
                  onClick={close}
                  data-testid="bulk-done"
                  className="inline-flex items-center gap-2 px-7 py-3 bg-burgundy hover:bg-maroon text-ivory text-xs uppercase tracking-[0.22em] transition-colors"
                >
                  Done
                </button>
              </div>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
}

// --- Main dashboard ---
export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const [products, setProducts] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [categories, setCategories] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cat, setCat] = useState('');
  const [tag, setTag] = useState('');
  const [q, setQ] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [catMgr, setCatMgr] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      invalidateCatalog();
      const [p, cnt, c, t] = await Promise.all([
        api.get('/products', { params: { category: cat || undefined, tag: tag || undefined, q: q || undefined } }),
        // Dedicated count endpoint — never truncated by pagination limits
        api.get('/products/count'),
        api.get('/categories'),
        api.get('/tags'),
      ]);
      setProducts(p.data);
      setTotalCount(cnt.data?.count ?? p.data.length);
      setCategories(c.data);
      setAllTags(t.data);
    } catch (e) { toast.error(formatApiError(e, 'Failed to load')); }
    finally { setLoading(false); }
  }, [cat, tag, q]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const remove = async (slug) => {
    if (!window.confirm('Delete this product? This cannot be undone.')) return;
    try { await api.delete(`/products/${slug}`); toast.success('Deleted'); loadAll(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const openAdd = () => { setEditing(null); setModalOpen(true); };
  const openEdit = async (p) => {
    // List endpoint omits the heavy `image` field; fetch full record so the
    // edit form can preview the current image.
    try {
      const { data } = await api.get(`/products/${p.slug}`);
      setEditing(data);
    } catch {
      setEditing(p);
    }
    setModalOpen(true);
  };

  const stats = useMemo(() => ({
    total: totalCount,
    categories: categories.length,
    tags: allTags.length,
  }), [totalCount, categories, allTags]);

  return (
    <main className="min-h-screen bg-ivory text-charcoal" data-testid="admin-dashboard">
      {/* Top bar */}
      <header className="border-b border-brandborder bg-surface sticky top-0 z-30">
        <div className="container-luxe flex items-center justify-between h-20">
          <div className="flex items-center gap-3">
            <Logo size="md" />
            <div>
              <div className="overline">Trendy Closet</div>
              <div className="font-display text-lg leading-tight">Management Dashboard</div>
            </div>
          </div>
          <div className="flex items-center gap-2 sm:gap-3">
            <Link to="/admin/traffic" className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 border border-brandborder hover:border-maroon hover:text-maroon text-[11px] sm:text-xs uppercase tracking-[0.18em] transition-colors" data-testid="admin-traffic-link">
              Traffic
            </Link>
            <Link to="/admin/wealth-planner" className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 border border-brandborder hover:border-maroon hover:text-maroon text-[11px] sm:text-xs uppercase tracking-[0.18em] transition-colors" data-testid="admin-wealth-planner-link">
              Wealth Planner
            </Link>
            <Link to="/admin/sold-cleanup" className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 border border-brandborder hover:border-maroon hover:text-maroon text-[11px] sm:text-xs uppercase tracking-[0.18em] transition-colors" data-testid="admin-sold-cleanup-link">
              Sold Cleanup
            </Link>
            <VtoAdminToggle />
            <Link to="/" className="hidden sm:inline-block text-xs uppercase tracking-[0.2em] text-charcoal/70 hover:text-maroon">View site →</Link>
            <span className="hidden md:inline text-sm text-charcoal/60">{user?.email}</span>
            <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 border border-brandborder hover:border-maroon hover:text-maroon text-[11px] sm:text-xs uppercase tracking-[0.18em] transition-colors">
              <LogOut className="w-4 h-4" /> <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      <section className="container-luxe py-10 lg:py-14">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          {[
            { k: 'Products', v: stats.total },
            { k: 'Collections', v: stats.categories },
            { k: 'Tags', v: stats.tags },
            { k: 'Owner', v: user?.name || 'Admin' },
          ].map((s) => (
            <div key={s.k} className="border border-brandborder bg-surface p-5">
              <div className="overline">{s.k}</div>
              <div className="font-display text-3xl text-maroon mt-1">{s.v}</div>
            </div>
          ))}
        </div>

        {/* Toolbar */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-5 mb-8">
          <div>
            <span className="overline">Collection</span>
            <h2 className="font-display text-3xl lg:text-4xl text-charcoal mt-2">Your closet, organised.</h2>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <button onClick={() => setBulkOpen(true)} data-testid="open-bulk-ai" className="inline-flex items-center gap-2 px-4 py-2.5 border border-maroon text-maroon hover:bg-maroon hover:text-ivory text-xs uppercase tracking-[0.18em] transition-colors">
              <Sparkles className="w-4 h-4" /> AI Bulk Upload
            </button>
            <button onClick={() => setCatMgr(true)} data-testid="open-categories" className="inline-flex items-center gap-2 px-4 py-2.5 border border-brandborder hover:border-maroon hover:text-maroon text-xs uppercase tracking-[0.18em] transition-colors"><FolderPlus className="w-4 h-4" /> Collections</button>
            <button onClick={openAdd} data-testid="add-product" className="inline-flex items-center gap-2 px-5 py-2.5 bg-burgundy hover:bg-maroon text-ivory text-xs uppercase tracking-[0.18em] transition-colors"><Plus className="w-4 h-4" /> Add product</button>
          </div>
        </div>

        {/* Filters */}
        <div className="border-y border-brandborder/70 py-5 mb-8 flex flex-col lg:flex-row gap-4 lg:items-center">
          <div className="flex items-center gap-2 flex-1 max-w-md">
            <Search className="w-4 h-4 text-charcoal/50" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, tag, fabric…" className={`${inputCls} py-2`} data-testid="search-input" />
          </div>
          <div className="flex flex-wrap gap-2">
            <button onClick={() => setCat('')} className={`px-4 py-2 text-xs uppercase tracking-[0.18em] border transition-colors ${!cat ? 'bg-burgundy text-ivory border-burgundy' : 'border-brandborder hover:border-maroon hover:text-maroon'}`}>All</button>
            {categories.map((c) => (
              <button key={c.key} onClick={() => setCat(c.key)} className={`px-4 py-2 text-xs uppercase tracking-[0.18em] border transition-colors ${cat === c.key ? 'bg-burgundy text-ivory border-burgundy' : 'border-brandborder hover:border-maroon hover:text-maroon'}`}>{c.label}</button>
            ))}
          </div>
        </div>

        {/* Tag chips */}
        {allTags.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mb-8">
            <span className="overline mr-2"><Tag className="w-3 h-3 inline-block mr-1" />Tags</span>
            <button onClick={() => setTag('')} className={`px-3 py-1 text-[11px] uppercase tracking-[0.15em] border transition-colors ${!tag ? 'border-maroon text-maroon' : 'border-brandborder text-charcoal/60 hover:border-maroon hover:text-maroon'}`}>any</button>
            {allTags.slice(0, 30).map((t) => (
              <button key={t.tag} onClick={() => setTag(tag === t.tag ? '' : t.tag)} className={`px-3 py-1 text-[11px] uppercase tracking-[0.15em] border transition-colors ${tag === t.tag ? 'border-maroon text-maroon bg-blush' : 'border-brandborder text-charcoal/60 hover:border-maroon hover:text-maroon'}`}>
                {t.tag} <span className="text-charcoal/40">·{t.count}</span>
              </button>
            ))}
          </div>
        )}

        {/* Grid */}
        {loading ? (
          <div className="py-20 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-maroon" /></div>
        ) : products.length === 0 ? (
          <div className="py-24 text-center border border-dashed border-brandborder">
            <p className="text-charcoal/60">No products match your filters.</p>
            <button onClick={openAdd} className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 bg-burgundy text-ivory text-xs uppercase tracking-[0.2em]"><Plus className="w-4 h-4" /> Add the first one</button>
          </div>
        ) : (
          <AnimatePresence>
            <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {products.map((p) => (
                <motion.div key={p.slug} layout initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }} className="bg-surface border border-brandborder group" data-testid={`admin-card-${p.slug}`}>
                  <div className="relative aspect-[3/4] overflow-hidden">
                    <img src={productImageUrl(p)} alt={p.label} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" decoding="async" />
                    <div className="absolute top-2 left-2 text-[10px] uppercase tracking-[0.18em] px-2 py-1 bg-ivory/90 backdrop-blur text-charcoal/80">{categories.find((c) => c.key === p.category)?.label || p.category}</div>
                    {p.is_category_hero && (
                      <div data-testid={`admin-hero-badge-${p.slug}`} className="absolute top-2 right-2 text-[10px] uppercase tracking-[0.18em] px-2 py-1 bg-gold text-charcoal">
                        Hero
                      </div>
                    )}
                    {p.sold && (
                      <div className="absolute inset-0 pointer-events-none" data-testid={`admin-sold-overlay-${p.slug}`}>
                        <div className="absolute inset-0 bg-charcoal/25" />
                        <div className="absolute top-6 -right-12 w-44 rotate-45 bg-burgundy text-ivory text-center py-1.5 text-[11px] uppercase tracking-[0.3em] shadow-md font-display">
                          Sold
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <div className="font-display text-lg leading-snug text-charcoal">{p.label}</div>
                    {p.sub && <div className="text-xs text-charcoal/55 mt-1 line-clamp-2">{p.sub}</div>}
                    {p.tags?.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-1">
                        {p.tags.slice(0, 4).map((t) => (
                          <span key={t} className="text-[10px] uppercase tracking-[0.12em] text-charcoal/55 border border-brandborder px-2 py-0.5">{t}</span>
                        ))}
                        {p.tags.length > 4 && <span className="text-[10px] text-charcoal/40">+{p.tags.length - 4}</span>}
                      </div>
                    )}
                    <div className="mt-4 flex items-center justify-between gap-2">
                      <Link to={`/product/${p.slug}`} target="_blank" className="text-[11px] uppercase tracking-[0.18em] text-charcoal/55 hover:text-maroon">View</Link>
                      <div className="flex items-center gap-1">
                        <button onClick={() => openEdit(p)} data-testid={`edit-${p.slug}`} className="p-2 text-charcoal/60 hover:text-maroon hover:bg-blush rounded-full" aria-label="Edit"><Edit3 className="w-4 h-4" /></button>
                        <button onClick={() => remove(p.slug)} data-testid={`delete-${p.slug}`} className="p-2 text-charcoal/60 hover:text-red-700 hover:bg-blush rounded-full" aria-label="Delete"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </AnimatePresence>
        )}
      </section>

      <ProductForm open={modalOpen} onClose={() => setModalOpen(false)} initial={editing} categories={categories} onSaved={loadAll} />
      <CategoriesManager open={catMgr} onClose={() => setCatMgr(false)} categories={categories} onChanged={loadAll} />
      <BulkAIUpload open={bulkOpen} onClose={() => setBulkOpen(false)} onSaved={loadAll} />
    </main>
  );
}

// -------- Virtual Try-On admin toggle (prototype) --------
const VtoAdminToggle = () => {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('/admin/virtual-tryon/settings').then(({ data }) => setSettings(data)).catch(() => setSettings(false));
  }, []);

  const toggle = async () => {
    if (!settings) return;
    setSaving(true);
    try {
      const { data } = await api.patch('/admin/virtual-tryon/settings', { enabled: !settings.enabled });
      setSettings(data);
      toast.success(`Virtual Try-On ${data.enabled ? 'enabled' : 'disabled'}`);
    } catch (e) {
      toast.error(formatApiError(e, 'Could not update Virtual Try-On settings'));
    } finally {
      setSaving(false);
    }
  };

  if (settings === null || settings === false) return null;
  const on = !!settings.enabled;
  return (
    <button
      onClick={toggle}
      disabled={saving}
      data-testid="admin-vto-toggle"
      title={on
        ? `Virtual Try-On ON — ${settings.stats?.generations_this_month ?? 0} this month`
        : 'Virtual Try-On OFF — tap to enable'}
      className={`inline-flex items-center gap-2 px-3 sm:px-4 py-2 border text-[11px] sm:text-xs uppercase tracking-[0.18em] transition-colors ${
        on
          ? 'border-maroon bg-maroon/10 text-maroon'
          : 'border-brandborder hover:border-maroon hover:text-maroon'
      } disabled:opacity-50`}
    >
      <Sparkles className="w-3.5 h-3.5" />
      <span>Try-On: {on ? 'ON' : 'OFF'}</span>
    </button>
  );
};
